import React from "react";
import Reveal from "./Reveal";

/**
 * The Structured Momentum Method.
 *
 * Six-step framework section that replaces the older
 * "Clarity Becomes Systems" arc on the homepage. Each step is
 * named, verbed, and described in human language. The visual
 * grammar matches the editorial charcoal-panel pattern used across
 * the site (bg-white/5 + border-gold/10 + gold hairline top).
 *
 * On desktop the six steps render as a 3x2 grid so the page does
 * not turn into a wide pipeline diagram. On mobile they stack.
 */

const STEPS = [
  {
    id: "listen",
    n: "01",
    label: "Listen",
    body:
      "We begin with where you actually are, what you are carrying, and what you are trying to move forward.",
  },
  {
    id: "locate",
    n: "02",
    label: "Locate",
    body:
      "We identify the real bottleneck, whether it is identity, clarity, positioning, structure, systems, execution, or alignment.",
  },
  {
    id: "clarify",
    n: "03",
    label: "Clarify",
    body:
      "We separate signal from noise so the next move becomes easier to see.",
  },
  {
    id: "structure",
    n: "04",
    label: "Structure",
    body:
      "We turn insight into an offer, plan, message, system, pathway, or decision.",
  },
  {
    id: "activate",
    n: "05",
    label: "Activate",
    body:
      "We use tools, relationships, AI, content, systems, and execution steps to create real movement.",
  },
  {
    id: "prove",
    n: "06",
    label: "Prove",
    body:
      "We document the result through visible proof, reflection, case studies, assets, or measurable progress.",
  },
];

const StructuredMomentumMethod = () => (
  <section
    id="structured-momentum-method"
    data-testid="structured-momentum-method-section"
    className="relative py-24 md:py-32 border-t border-gold/10"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
          The Method
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center max-w-3xl mx-auto">
          The Structured{" "}
          <span className="italic gold-gradient-text">Momentum Method.</span>
        </h2>
        <p className="mt-7 max-w-2xl mx-auto text-center text-white/85 leading-relaxed">
          Momentum becomes sustainable when insight is translated into structure. This is how a person moves from inner clarity to outer execution.
        </p>
      </Reveal>

      <div
        data-testid="structured-momentum-grid"
        className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
      >
        {STEPS.map((s, i) => (
          <Reveal key={s.id} delay={100 + i * 70}>
            <article
              data-testid={`smm-step-${s.id}`}
              className="bg-white/5 border-t border-gold/20 p-7 md:p-8 rounded-lg border border-gold/10 h-full"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-3xl text-gold">{s.n}</span>
                <h3 className="font-serif text-2xl text-themed font-light leading-tight">
                  {s.label}
                </h3>
              </div>
              <p className="mt-5 text-white/85 text-[15.5px] leading-relaxed">
                {s.body}
              </p>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={300}>
        <p
          data-testid="smm-arc-line"
          className="mt-14 text-center text-[11px] sm:text-[12px] tracking-[0.22em] uppercase text-gold/85"
        >
          From identity to clarity. From clarity to structure. From structure to execution. From execution to proof.
        </p>
      </Reveal>
    </div>
  </section>
);

export default StructuredMomentumMethod;
